import $ from "jquery";
import axios from "axios";
import { omit } from "ramda";
import { setupCache } from "axios-cache-adapter";

const cache = setupCache({
  maxAge: 60 * 60 * 1000,
});

const baseUrl =
  process.env.NODE_ENV === "development"
    ? "http://api.cloudcomments.sl/api"
    : process.env.DEPLOY === "prod"
    ? "https://app.cloudcomments.ru/api"
    : "https://demo.cloudcomments.ru/api";

const api = axios.create({
  baseURL: baseUrl,
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
  adapter: cache.adapter,
});

/**
 * @type {Object<number, ProductResponse>}
 */
let cachedProductsByUid = {};

/**
 * Получение данных продуктов
 * @param {string[]} uids
 * @return {Promise<ProductResponse[]>}
 */

let getProductsPromise = Promise.resolve([]);

export function getProducts(uids) {
  return (getProductsPromise = getProductsPromise.then(function () {
    if (!uids.length) return Promise.resolve([]);

    const cachedProductsUids = Object.keys(cachedProductsByUid);
    const notCachedProductsUids = uids
      .filter(function (id) {
        return !cachedProductsUids.includes(id);
      })
      .filter(Boolean);
    const cachedProducts = Object.values(cachedProductsByUid).filter(function (
      p
    ) {
      return uids.includes(p?.uid);
    });

    if (!notCachedProductsUids.length) return Promise.resolve(cachedProducts);

    return api
      .get(`${baseUrl}/products/?products=${notCachedProductsUids.join(",")}`)
      .then(({ data: products }) => {
        notCachedProductsUids.forEach(function (uid) {
          cachedProductsByUid = {
            ...cachedProductsByUid,
            [uid]: products.find((p) => p.uid === uid),
          };
        });

        return [...cachedProducts, ...products];
      });
  }));
}

/**
 * Получение отзывов товара
 * @param {Product['uid']} uid
 * @param {number} page
 * @param {'useful' | 'date' | 'hight_grade' | 'low_grade'} sorting
 * @return {Promise<{reviews: PaginationResponse<ReviewResponse>; user_likes: number[], user_dislikes: number[]}>}
 */

export async function getReviews(uid, page = 1, sorting = "useful") {
  return await api
    .get(`${baseUrl}/reviews/${uid}/?page=${page}&sorting=${sorting}`, {
      cache: { maxAge: 0, ignoreCache: true },
    })
    .then((r) => r.data);
}

/**
 * Отправить отзыв
 * @param {Review} review
 * @param {Product} product
 * @return
 */

export async function createReview(review, product) {
  return await api
    .post(`${baseUrl}/reviews/`, {
      ...review,
      ...omit(["uid"], product),
      product_uid: product.uid,
    })
    .then((r) => r.data);
}

/**
 * Поставить/Удалить лайк
 * @param {number} review_id
 * @return
 */

export async function toggleLike(review_id) {
  return await api
    .post(`${baseUrl}/like/`, {
      review_id,
    })
    .then((r) => r.data);
}

/**
 * Поставить/Удалить лайк
 * @param {number} review_id
 * @return {Promise<any>}
 */

export async function toggleDislike(review_id) {
  return await api
    .post(`${baseUrl}/dislike/`, {
      review_id,
    })
    .then((r) => r.data);
}

/**
 * Получить список ограничений
 * @return {Promise<ShopLimitation[]>}
 */

export async function getLimitations() {
  return await api.get(`${baseUrl}/limitations`).then((r) => r.data);
}

/**
 * Get shop settings
 */
export async function getShopSettings() {
  return await api.get(`${baseUrl}/shop-settings`);
}

/**
 * Загрузить файл
 * @param {File} file
 * @return {Promise<ReviewImage>}
 */
export async function uploadFile(file) {
  const data = new FormData();
  data.append("image", file);
  return await api.post(`${baseUrl}/files/upload`, data).then((r) => r.data);
}
